// frontend/src/pages/Pricing.js
//
// Simple two-tier pricing page. Public route — accessible to signed-out
// visitors AND signed-in users. Design goal: minimal, visually calm,
// nothing crowded. Two cards, one comparison line, single CTA per card.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import posthog from 'posthog-js';
import axios from '../api/axiosConfig';
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const PRICE = '$0.99';
const PRICE_PERIOD = '/month';

const FREE_FEATURES = [
  'Save browser sessions',
  'Restore on any device',
  'Ask AI across your research',
  'One browser',
];

const PRO_FEATURES = [
  'Everything in Free',
  'Unlimited devices + browsers',
  'Cross-browser sync (Safari iOS ↔ Chrome desktop)',
  'Unlimited AI conversation capture',
  'Priority support',
];

export default function Pricing() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  // Funnel: capture pricing page view once per session for retention/attribution analysis
  useEffect(() => {
    try {
      posthog.capture('pricing_viewed', {
        signed_in: !!currentUser,
        referrer: document.referrer || 'direct',
      });
    } catch { /* posthog may not be initialized */ }
  }, [currentUser]);

  const handleGoPro = useCallback(async () => {
    // Funnel: fires whether user is signed in or not — lets us measure both
    // "went to signup from pricing" and "started checkout from pricing" as
    // separate funnel steps.
    try {
      posthog.capture('checkout_started', {
        signed_in: !!currentUser,
        cta: currentUser ? 'upgrade_to_pro' : 'start_pro_trial',
      });
    } catch { /* posthog may not be initialized */ }
    if (!currentUser) {
      // Record that this signup was initiated from Pricing so we can
      // attribute the resulting signup back to this page.
      try { sessionStorage.setItem('signup_source', 'pricing'); } catch { /* ignore */ }
      navigate('/signup?next=/pricing');
      return;
    }
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await axios.post(
        '/api/billing/create-checkout',
        { successUrl: `${window.location.origin}/settings`, cancelUrl: `${window.location.origin}/pricing` },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      try {
        posthog.capture('checkout_failed', {
          status: err.response?.status,
          message: (err.response?.data?.error || err.message || 'unknown').slice(0, 200),
        });
      } catch { /* posthog may not be initialized */ }
      setCheckoutError(err.response?.data?.error || err.message || 'Checkout unavailable — try again in a moment.');
      setCheckoutLoading(false);
    }
  }, [currentUser, navigate]);

  return (
    <>
      <Helmet>
        <title>Pricing — Blackmount</title>
        <meta name="description" content="Blackmount pricing — Free forever, or Pro at $0.99/month for unlimited devices and cross-browser sync." />
      </Helmet>

      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md">

          {/* Hero */}
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 10 } }}>
            <Typography
              variant={isMobile ? 'h4' : 'h3'}
              component="h1"
              fontWeight={700}
              sx={{ mb: 2, color: 'text.primary' }}
            >
              Simple pricing
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 480, mx: 'auto', fontWeight: 400 }}
            >
              Free forever for one browser. Pro unlocks unlimited devices and cross-browser sync.
            </Typography>
          </Box>

          {/* Tier cards — side by side on md+, stacked on mobile */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            alignItems="stretch"
            justifyContent="center"
            sx={{ mb: 6 }}
          >

            {/* FREE */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { md: 360 },
                p: { xs: 4, md: 5 },
                bgcolor: 'background.paper',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="overline" color="text.secondary" sx={{ mb: 1, letterSpacing: 1 }}>
                Free
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 4 }}>
                <Typography variant="h3" fontWeight={700} sx={{ color: 'text.primary' }}>
                  $0
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                  forever
                </Typography>
              </Box>
              <Stack spacing={1.5} sx={{ mb: 4, flex: 1 }}>
                {FREE_FEATURES.map(f => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CheckIcon sx={{ fontSize: 20, color: 'text.secondary', mt: 0.25 }} />
                    <Typography variant="body2" color="text.primary">{f}</Typography>
                  </Box>
                ))}
              </Stack>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => currentUser ? navigate('/sessions') : navigate('/signup')}
                sx={{ borderRadius: 2, textTransform: 'none', py: 1.25, fontSize: '0.95rem' }}
              >
                {currentUser ? 'Open Blackmount' : 'Get started free'}
              </Button>
            </Box>

            {/* PRO — highlighted */}
            <Box
              sx={{
                flex: 1,
                maxWidth: { md: 360 },
                p: { xs: 4, md: 5 },
                bgcolor: 'background.paper',
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: 3,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                boxShadow: '0 8px 24px rgba(37,99,235,0.10)',
              }}
            >
              <Chip
                label="POPULAR"
                size="small"
                sx={{
                  position: 'absolute',
                  top: -12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  letterSpacing: 1,
                }}
              />
              <Typography variant="overline" color="primary.main" sx={{ mb: 1, letterSpacing: 1, fontWeight: 600 }}>
                Pro
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 4 }}>
                <Typography variant="h3" fontWeight={700} sx={{ color: 'text.primary' }}>
                  {PRICE}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ ml: 1 }}>
                  {PRICE_PERIOD}
                </Typography>
              </Box>
              <Stack spacing={1.5} sx={{ mb: 4, flex: 1 }}>
                {PRO_FEATURES.map(f => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <CheckIcon sx={{ fontSize: 20, color: 'primary.main', mt: 0.25 }} />
                    <Typography variant="body2" color="text.primary">{f}</Typography>
                  </Box>
                ))}
              </Stack>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={checkoutLoading ? <CircularProgress size={18} color="inherit" /> : <BoltIcon />}
                onClick={handleGoPro}
                disabled={checkoutLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  py: 1.25,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  bgcolor: 'primary.main',
                }}
              >
                {checkoutLoading ? 'Opening checkout…' : (currentUser ? 'Upgrade to Pro' : 'Start Pro trial')}
              </Button>
              {checkoutError && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1.5, textAlign: 'center' }}>
                  {checkoutError}
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Trust line — single sentence, no clutter */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', maxWidth: 480, mx: 'auto', fontSize: '0.9rem' }}
          >
            Cancel anytime. No credit card required for Free.
          </Typography>

        </Container>
      </Box>
    </>
  );
}
